import React from 'react';
import { X, Plus, Info } from 'lucide-react';
import { IDField } from './IDField';
import { DiscographyId, DiscographySourceType } from '../useDiscographyIds';
import WButton from 'shared_components/components/WForms/WButton/WButton';
import { usePrimaryDiscographyRun } from '../usePrimaryDiscographyRun';

export interface AliasId {
  source: DiscographySourceType;
  id: string;
  itemId: string;
  originalId?: string;
  validating?: boolean;
  validated?: boolean;
  valid?: boolean;
  rejected?: boolean;
  approved?: boolean;
  error?: string | null;
  artistName?: string;
  originalArtistName?: string;
  editing?: boolean;
  created_by_system: boolean;
}

export interface AliasForm {
  id?: string;
  aliasName: string;
  isGroup: boolean;
  isPermanentMember: boolean;
  ids: AliasId[];
  manualStageName: boolean;
  hasAutoPopulated: boolean;
  errors: Record<string, string>;
}

interface AliasModalProps {
  show: boolean;
  mode: 'create' | 'edit';
  onClose: () => void;
  formData: AliasForm;
  activeTab: string;
  onTabChange: (tab: DiscographySourceType) => void;
  onItemsChange: (updatedItem: AliasId) => void;
  onRemoveItem: (removedItem: AliasId) => void;
  onNameChange: (newName: string) => void;
  onAddIdField: () => void;
  onSubmit: () => void;
}

const AliasModal: React.FC<AliasModalProps> = ({
  show,
  mode = 'create',
  onClose,
  formData,
  activeTab,
  onTabChange,
  onItemsChange,
  onRemoveItem,
  onNameChange,
  onAddIdField,
  onSubmit,
}) => {
  const { actionsDisabled } = usePrimaryDiscographyRun();

  if (!show) return null;

  const showDiscogsMultiplIDsWarning =
    formData.ids.filter((id) => id.source === 'discogs').length > 1;

  return (
    <div className="tw-fixed tw-inset-0 tw-bg-black tw-bg-opacity-70 tw-flex tw-items-center tw-justify-center tw-z-50">
      <div className="tw-bg-gray-800 tw-rounded-lg tw-max-h-[90vh] tw-flex tw-flex-col tw-max-w-xl tw-w-full tw-mx-4">
        <div className="tw-flex tw-justify-between tw-items-center tw-p-3 tw-border-b tw-border-gray-700">
          <h3 className="tw-text-white tw-text-lg tw-font-medium">
            {mode === 'create' ? 'Add Alias' : 'Update Alias'}
          </h3>
          <button
            onClick={onClose}
            className="tw-text-gray-400 hover:tw-text-white"
          >
            <X size={20} />
          </button>
        </div>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            onSubmit();
          }}
        >
          <div className="tw-overflow-y-auto tw-p-3 tw-flex-grow">
            {formData.errors.general && (
              <div className="tw-bg-westburyRed/50 tw-border tw-border-westburyRed tw-text-westburyRed tw-p-2 tw-mb-3 tw-rounded-md">
                {formData.errors.general}
              </div>
            )}
            <div className="tw-space-y-3">
              {/* Alias Name */}
              <div>
                <label className="tw-text-white tw-mb-1 tw-flex tw-items-center">
                  Alias Name
                  {formData.aliasName ? (
                    <span
                      className={`tw-ml-2 tw-text-xs tw-px-2 tw-py-0.5 tw-rounded-full ${
                        formData.manualStageName
                          ? 'tw-bg-westburyAmber/50 tw-text-westburyAmber'
                          : 'tw-bg-westburyBlue/50 tw-text-westburyBlue'
                      }`}
                    >
                      {formData.manualStageName
                        ? 'Manually entered'
                        : `Auto-populated`}
                    </span>
                  ) : null}
                </label>
                <input
                  disabled={actionsDisabled}
                  type="text"
                  name="aliasName"
                  value={formData.aliasName}
                  onChange={(e) => onNameChange(e.target.value)}
                  className={`tw-w-full tw-bg-gray-700 tw-border ${
                    formData.errors.aliasName
                      ? 'tw-border-westburyRed'
                      : 'tw-border-gray-600'
                  } tw-rounded-md tw-px-3 tw-py-1.5 tw-text-white tw-outline-none`}
                  required
                />
                {formData.errors.aliasName && (
                  <p className="tw-text-westburyRed tw-text-xs tw-mt-1">
                    {formData.errors.aliasName}
                  </p>
                )}
              </div>
            </div>
          </div>
          {/* IDs Section */}
          <div className="tw-flex tw-flex-col tw-flex-grow tw-border-t tw-border-gray-700 tw-p-3">
            <div className="tw-flex tw-justify-between tw-items-center tw-mb-1">
              <div className="tw-block tw-text-white">Discography IDs</div>
              {!actionsDisabled ? (
                <WButton
                  onClick={onAddIdField}
                  variant="secondary"
                  label="+ Add ID"
                />
              ) : null}
            </div>
            {/* Tabs for source selection */}
            <div className="tw-flex tw-border-b tw-border-gray-700 tw-mb-2">
              <button
                type="button"
                onClick={() => onTabChange('discogs')}
                className={`tw-px-3 tw-py-1.5 tw-text-sm ${
                  activeTab === 'discogs'
                    ? 'tw-text-westburyBlue tw-border-b-2 tw-border-westburyBlue'
                    : 'tw-text-gray-400'
                }`}
              >
                Discogs
              </button>
              <button
                type="button"
                onClick={() => onTabChange('deezer')}
                className={`tw-px-3 tw-py-1.5 tw-text-sm ${
                  activeTab === 'deezer'
                    ? 'tw-text-westburyBlue tw-border-b-2 tw-border-westburyBlue'
                    : 'tw-text-gray-400'
                }`}
              >
                Deezer
              </button>
              <button
                type="button"
                onClick={() => onTabChange('spotify')}
                className={`tw-px-3 tw-py-1.5 tw-text-sm ${
                  activeTab === 'spotify'
                    ? 'tw-text-westburyBlue tw-border-b-2 tw-border-westburyBlue'
                    : 'tw-text-gray-400'
                }`}
              >
                Spotify
              </button>
              <button
                type="button"
                onClick={() => onTabChange('musicbrainz')}
                className={`tw-px-3 tw-py-1.5 tw-text-sm ${
                  activeTab === 'musicbrainz'
                    ? 'tw-text-westburyBlue tw-border-b-2 tw-border-westburyBlue'
                    : 'tw-text-gray-400'
                }`}
              >
                MusicBrainz
              </button>
            </div>
            <div className="tw-h-[300px] tw-overflow-y-auto tw-rounded-md">
              {showDiscogsMultiplIDsWarning && activeTab === 'discogs' ? (
                <div className="tw-bg-westburyBlue/50 tw-border tw-border-westburyBlue tw-text-westburyBlue tw-p-2 tw-mb-3 tw-rounded-md tw-inline-flex tw-gap-2 tw-items-center tw-w-full tw-text-sm">
                  <Info size={16} />
                  <span>{'Discogs ID already linked'}</span>
                </div>
              ) : null}
              {formData.ids
                .filter((id) => id.source === activeTab)
                .map((item) => (
                  <IDField
                    disabled={actionsDisabled}
                    key={item.itemId}
                    source={activeTab}
                    items={formData.ids}
                    item={item}
                    allowDelete={
                      formData.ids.filter((id) => id.source === activeTab)
                        .length > 0
                    }
                    onRemove={onRemoveItem}
                    onChange={onItemsChange}
                  />
                ))}
            </div>
          </div>
        </form>
        <div className="tw-flex tw-justify-end tw-space-x-3 tw-p-3 tw-border-t tw-border-gray-700">
          {!actionsDisabled ? (
            <>
              <WButton onClick={onClose} label="Cancel" variant="secondary" />
              <WButton
                onClick={onSubmit}
                variant="primary"
                label={mode === 'create' ? 'Add' : 'Save'}
              />
            </>
          ) : (
            <WButton onClick={onClose} label="Close" variant="secondary" />
          )}
        </div>
      </div>
    </div>
  );
};
export default AliasModal;
